import { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { eventsAPI } from '../../lib/api';
import { t } from '../../lib/i18n';
import { btnGhost, EmptyState, inputCls, panelCls, StatusBadge } from '../../components/ui';
import { ProductBadge, ProductFilter } from '../../components/products';
import { nyTime } from '../../lib/nyTime';

export default function EventsPage() {
  const [data, setData] = useState(null);
  const [type, setType] = useState('');
  const [product, setProduct] = useState('');
  const [page, setPage] = useState(1);

  const load = useCallback(() => {
    eventsAPI.list({ type, product, page, per: 25 })
      .then(({ data }) => setData(data))
      .catch(() => toast.error(t('Could not load events')));
  }, [type, product, page]);
  useEffect(load, [load]);

  const pages = Math.max(1, Math.ceil((data?.total || 0) / 25));

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-lg font-semibold">{t('Events')}</h1>
        <button onClick={load} className={btnGhost}><RefreshCw size={14} /> {t('Refresh')}</button>
      </div>

      <div className="mb-3">
        <ProductFilter value={product} onChange={(v) => { setPage(1); setProduct(v); }} />
      </div>
      <div className="mb-3">
        <input className={`${inputCls} !w-64`} placeholder={t('Filter by type…')} value={type}
          onChange={(e) => { setPage(1); setType(e.target.value); }} />
      </div>

      {data === null ? (
        <p className="text-sm text-mms-muted">{t('Loading…')}</p>
      ) : data.items.length === 0 ? (
        <EmptyState>{t('No events yet — they arrive from the platform, LMS and KMS hooks.')}</EmptyState>
      ) : (
        <div className={`${panelCls} overflow-x-auto`}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-mms-border text-left text-xs text-mms-faint">
                <th className="px-4 py-2.5 font-medium">{t('When')}</th>
                <th className="px-4 py-2.5 font-medium">{t('Type')}</th>
                <th className="px-4 py-2.5 font-medium">{t('Product')}</th>
                <th className="px-4 py-2.5 font-medium">{t('Member')}</th>
                <th className="px-4 py-2.5 font-medium">{t('Subject')}</th>
                <th className="px-4 py-2.5 font-medium">{t('Rule results')}</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((ev) => (
                <tr key={ev.id} className="border-b border-mms-border/60 last:border-0 align-top">
                  <td className="px-4 py-2.5 whitespace-nowrap text-xs text-mms-muted">
                    {nyTime(ev.received_at)}
                  </td>
                  <td className="px-4 py-2.5"><code className="text-xs">{ev.type}</code></td>
                  <td className="px-4 py-2.5"><ProductBadge product={ev.product} /></td>
                  <td className="px-4 py-2.5">
                    {ev.member?.name || '—'}
                    {ev.member?.membership_number ? (
                      <span className="ml-1 text-xs text-mms-faint">#{ev.member.membership_number}</span>
                    ) : null}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-mms-muted">{ev.subject || '—'}</td>
                  <td className="px-4 py-2.5">
                    {(ev.results || []).length === 0 ? (
                      <span className="text-xs text-mms-faint">{t('no matching rules')}</span>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {ev.results.map((r, i) => (
                          <span key={i} className="inline-flex items-center gap-1 text-xs">
                            <StatusBadge status={r.status} />
                            <span className="text-mms-muted">{r.rule_name}</span>
                            {r.status === 'awarded' && (
                              <span className="text-mms-faint mms-num">
                                ({(r.awards || []).map((a) => `${a.amount} ${t(a.currency)}`).join(', ')})
                              </span>
                            )}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="mt-3 flex items-center justify-end gap-2 text-sm text-mms-muted">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className={btnGhost}>{t('Previous')}</button>
          <span className="mms-num">{page} / {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className={btnGhost}>{t('Next')}</button>
        </div>
      )}
    </div>
  );
}
